import type {
  CustomerDebtCollection,
  CustomerPayment,
  CustomerScheduledPayment,
  CustomerSubscriptionRecovery,
  DashboardCustomer,
  DashboardCustomerPaymentMethod,
} from '@models/customers';
import type { PaymentCurrency } from '@models/payments';
import { PaymentCardNetworks, PaymentMethodTypes } from '@models/payments';
import { formatPaymentAmount } from '@features/payments/helpers';
import { formatCardExpiry, formatMaskedCardDigits } from '@shared/utils';

import { formatDistanceToNow } from 'date-fns';

import type { StatusChipTone } from '@components/ui/StatusChip';

import {
  CUSTOMER_PAYMENT_TYPE_LABELS,
  SCHEDULED_PAYMENT_STATUS_LABELS,
  ScheduledPaymentChargerTypes,
  SubscriptionStates,
  type SubscriptionState,
} from './constants';

// Re-exported so customer components import every helper from this one barrel.
export { formatShortDate, formatMaskedCardDigits } from '@shared/utils';

const EURO_CURRENCY: PaymentCurrency = {
  id: '',
  code: 'EUR',
  symbol: '€',
  decimalDigits: 2,
};

export function getCustomerDisplayName(customer: DashboardCustomer): string {
  return [customer.firstName, customer.lastName].filter(Boolean).join(' ') || customer.email || '';
}

// A customer is considered migrated (from the legacy system) when it carries metadata.
export function isMigratedCustomer(customer: DashboardCustomer): boolean {
  const gatewayMigration = customer.metadata?.gatewayMigration;
  return !!gatewayMigration;
}

export function isNutramegTestCustomer(customer: DashboardCustomer): boolean {
  return customer.email.toLowerCase().endsWith('@nutrameg.com');
}

export function getCustomerInitials(customer: DashboardCustomer): string {
  const name = getCustomerDisplayName(customer);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export const AVATAR_COLOR_COUNT = 6;

/** Stable palette index for a customer's avatar, derived from their id. */
export function getCustomerAvatarColorIndex(customer: DashboardCustomer): number {
  let hash = 0;
  const source = customer.id || customer.email || '';
  for (let i = 0; i < source.length; i++) {
    hash = (hash * 31 + source.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % AVATAR_COLOR_COUNT;
}

export function getCustomerSubscriptionState(customer: DashboardCustomer): SubscriptionState {
  const subscriptions = customer.subscriptions ?? [];
  if (subscriptions.length === 0) return SubscriptionStates.NONE;
  return subscriptions.some((sub) => !sub.canceledAt)
    ? SubscriptionStates.ACTIVE
    : SubscriptionStates.CANCELED;
}

export function getActiveSubscriptions(customer: DashboardCustomer) {
  const activeSubs = (customer.subscriptions ?? []).filter((sub) => !sub.canceledAt);

  return activeSubs;
}

export function getSubscriptionLabel(item: {
  offerType?: string | null;
  features?: string[] | null;
}): string {
  const offerType = item.offerType?.trim();
  if (offerType) return offerType;
  return (item.features ?? []).join(', ');
}

export function getCustomerOutstandingDebt(customer: DashboardCustomer): number {
  return (customer.debtCollections ?? [])
    .filter((debt) => !debt.collectedAt)
    .reduce((sum, debt) => sum + (debt.amountInEuro ?? 0), 0);
}

// Debts, payments, refunds, etc. reference their currency by `currencyId`; only scheduled payments embed a
// full `currency` object. `customer.currencies` lists every currency any of the customer's rows reference,
// so this lookup can format those native amounts in their own currency instead of assuming EUR. Scheduled
// payments' embedded currency is still folded in as a fallback in case it carries an id not in that list.
export function buildCurrencyLookup(customer: DashboardCustomer): Map<string, PaymentCurrency> {
  const map = new Map<string, PaymentCurrency>();
  for (const currency of customer.currencies ?? []) {
    map.set(currency.id, currency);
  }
  for (const sp of customer.scheduledPayments ?? []) {
    if (sp.currency) map.set(sp.currency.id, sp.currency);
  }
  return map;
}

// Format a native minor-unit amount in the currency referenced by `currencyId`. Payments, refunds, debts
// and recovery amounts are all stored in their own charge currency (not euro), so formatting them as EUR
// would print the native figure under a € sign. Falls back to EUR only when the currency can't be resolved
// from the customer payload.
export function formatAmountWithCurrency(
  minorUnits: number,
  currencyId: string | null | undefined,
  currencyById: Map<string, PaymentCurrency>,
): string {
  const currency = currencyId ? currencyById.get(currencyId) : undefined;
  return currency
    ? formatPaymentAmount(minorUnits, currency, currency.decimalDigits)
    : formatEuroAmount(minorUnits);
}

// Format a debt's native amount (`amount` / `collectedAmount`) in its own currency.
export function formatDebtAmount(
  minorUnits: number,
  debt: CustomerDebtCollection,
  currencyById: Map<string, PaymentCurrency>,
): string {
  return formatAmountWithCurrency(minorUnits, debt.currencyId, currencyById);
}

// A payment's charge amount in the currency the customer was actually charged in. Unlike the other native
// amounts, a payment always carries `amountInEuro`, so an unresolvable currency falls back to the
// euro-normalized amount — a correct value under a correct symbol — instead of mislabeling the native
// figure as EUR.
export function formatCustomerPaymentAmount(
  payment: CustomerPayment,
  currencyById: Map<string, PaymentCurrency>,
): string {
  const currency = currencyById.get(payment.currencyId);
  return currency
    ? formatPaymentAmount(payment.amount, currency, currency.decimalDigits)
    : formatEuroAmount(payment.amountInEuro);
}

export function getFailedDebtCollectionsCount(customer: DashboardCustomer): number {
  return (customer.debtCollections ?? []).filter((debt) => debt.status === 'failed').length;
}

export function getCustomerRefundedTotal(customer: DashboardCustomer): number {
  return (customer.payments ?? []).reduce((sum, p) => sum + (p.amountRefunded ?? 0), 0);
}

export function formatEuroAmount(minorUnits: number): string {
  return formatPaymentAmount(minorUnits, EURO_CURRENCY, EURO_CURRENCY.decimalDigits);
}

export function formatMinorAmount(minorUnits: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(minorUnits / 100);
  } catch {
    return `${(minorUnits / 100).toFixed(2)} ${currency}`;
  }
}

export function getCustomerCountry(customer: DashboardCustomer): string {
  return customer.country || customer.originOrderPaymentIPCountry || '';
}

export function getCountryName(code: string): string {
  if (!code) return '';
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase()) || code;
  } catch {
    return code;
  }
}

export function humanizeLabel(value: string): string {
  const text = value.replace(/_/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function getCustomerPaymentTypeLabel(collectionType: string | null): string {
  if (!collectionType) return 'Recurring';
  return CUSTOMER_PAYMENT_TYPE_LABELS[collectionType] ?? collectionType;
}

export function getScheduledPaymentStatusLabel(status: string): string {
  return SCHEDULED_PAYMENT_STATUS_LABELS[status] ?? humanizeLabel(status.replace(/-/g, ' '));
}

export function getScheduledPaymentStatusTone(status: string): StatusChipTone {
  if (status === 'succeeded') return 'success';
  if (status === 'failed' || status === 'failed-and-rescheduled') return 'danger';
  if (status === 'upcoming' || status === 'processing') return 'warning';
  return 'neutral';
}

// Statuses for which the charge is still due to run — the only ones a countdown is meaningful for.
const SCHEDULED_PAYMENT_PENDING_STATUSES = new Set([
  'upcoming',
  'processing',
  'failed-and-rescheduled',
]);

// Relative countdown to a scheduled payment's execution ("in 3 days"), or '—' once it's settled.
export function getScheduledPaymentTimeLeft(status: string, executeAt: string): string {
  if (!SCHEDULED_PAYMENT_PENDING_STATUSES.has(status)) return '—';
  return formatDistanceToNow(new Date(executeAt), { addSuffix: true });
}

// A scheduled payment's charger label with its subtype: "Recurring v2", "Recurring v2 (RPR)" for a
// subscription-recovery charge, "Recurring v2 (debt)" for a debt-collection charge, "Offset v2", or
// "Legacy" for pre-v2 charges that carry no charger type.
export function getScheduledPaymentChargerLabel(
  scheduledPayment: Pick<
    CustomerScheduledPayment,
    'chargerType' | 'chargerVersion' | 'debtCollectionId' | 'subscriptionRecoveryId'
  >,
): string {
  const { chargerType, chargerVersion, debtCollectionId, subscriptionRecoveryId } =
    scheduledPayment;
  if (!chargerType) return 'Legacy';

  const base = chargerType === ScheduledPaymentChargerTypes.OFFSET ? 'Offset' : 'Recurring';
  const version = chargerVersion ? ` v${chargerVersion}` : '';

  let subtype = '';
  if (subscriptionRecoveryId) {
    subtype = ' (RPR)';
  } else if (chargerType === ScheduledPaymentChargerTypes.DEBT_COLLECTION || debtCollectionId) {
    subtype = ' (debt)';
  }

  return `${base}${version}${subtype}`;
}

// The row's amount, in the currency the customer is actually billed in.
//
// If a payment exists (chargedAmount != null), a charge actually ran — show what it charged, whatever the
// row type. A failed row can have no payment (declined before any charge), so we key off the payment, not
// the status. Rows that haven't produced a payment show an expected figure: debt shows the per-cycle charge
// and outstanding balance, recovery shows the balance still to recover, plain rows the recurring price
// (scheduledPayment.amount falls back to it while the row has no executed session of its own).
export function getScheduledPaymentAmountLabel(
  scheduledPayment: CustomerScheduledPayment,
  debt?: CustomerDebtCollection,
  recovery?: CustomerSubscriptionRecovery,
): string {
  const currency = scheduledPayment.currency;
  if (!currency) return '—';

  const format = (minorUnits: number) =>
    formatPaymentAmount(minorUnits, currency, currency.decimalDigits);

  // A payment exists: show what it actually charged, whatever the row type.
  if (scheduledPayment.chargedAmount != null) {
    return format(scheduledPayment.chargedAmount);
  }

  // No payment yet (or the row failed before charging) — show the expected figure.

  // Recovery attempts size themselves at charge time, so show the balance still to recover.
  if (recovery && recovery.originalChargeAmount != null) {
    const remaining = Math.max(
      recovery.originalChargeAmount - (recovery.totalRecoveredAmount ?? 0),
      0,
    );
    if (remaining > 0) return `${format(remaining)} to recover`;
  }

  // Debt collects one cycle (debt.amount) per attempt; the rest of the balance is still outstanding.
  if (debt) {
    const outstanding = debt.amount * Math.max(debt.cycleCount - debt.succeededCount, 0);
    return outstanding > debt.amount
      ? `${format(debt.amount)} (of ${format(outstanding)} outstanding)`
      : format(debt.amount);
  }

  // Plain recurring/offset: the scheduled charge (its session amount is the recurring price).
  return scheduledPayment.amount != null ? format(scheduledPayment.amount) : '—';
}

export type PaymentMethodBrand =
  | 'visa'
  | 'mastercard'
  | 'paypal'
  | 'googlepay'
  | 'applepay'
  | 'card';

/** Accepts both `DashboardCustomerPaymentMethod` and payments' `CustomerPaymentMethod`. */
export function getPaymentMethodBrand(method: {
  type?: string | null;
  network?: string | null;
  lastDigits?: string | null;
  legalCompany?: string | null;
}): PaymentMethodBrand {
  const type = (method.type ?? '').toUpperCase();
  // Wallets first — wallet-tokenized methods still carry a card network (VISA/MASTERCARD).
  if (type.includes(PaymentMethodTypes.GOOGLE_PAY)) return 'googlepay';
  if (type.includes(PaymentMethodTypes.APPLE_PAY)) return 'applepay';
  const network = (method.network ?? '').toUpperCase();
  if (network.includes(PaymentCardNetworks.VISA)) return 'visa';
  if (network.includes(PaymentCardNetworks.MASTERCARD)) return 'mastercard';
  if (type.includes('PAYPAL') || network.includes('PAYPAL')) return 'paypal';
  // PayPal billing agreements vault an account email instead of card digits.
  if (!method.lastDigits && method.legalCompany?.includes('@')) return 'paypal';
  return 'card';
}

// Masked digits ("•••• 7210") of the payment method used by an embedded payment, if known.
export function getCustomerPaymentMethodDigits(
  customer: DashboardCustomer,
  payment: CustomerPayment,
): string {
  const method = (customer.customerPaymentMethods ?? []).find(
    (m) => m.id === payment.customerPaymentMethodId,
  );
  return formatMaskedCardDigits(method?.lastDigits, { fallback: 'N/A' });
}

// Title for a payment method row: masked digits, else PayPal, else the provider name.
export function getPaymentMethodLabel(method: DashboardCustomerPaymentMethod): string {
  if (method.lastDigits) return formatMaskedCardDigits(method.lastDigits);
  if (getPaymentMethodBrand(method) === 'paypal') return 'PayPal';
  return method.provider || 'Payment method';
}

// Secondary line for a payment method row: expiry date, else legal company / provider.
export function getPaymentMethodSubLabel(method: DashboardCustomerPaymentMethod): string {
  if (method.expiresAt) return `Expires ${formatCardExpiry(method.expiresAt)}`;
  return method.legalCompany || method.provider || '';
}
